import React, { useEffect, useMemo, useRef, useState } from "react";
import { GET_WITH_TOKEN } from "../../Backend/Bakend";
import { showToast } from "../Constent/Toast";

const API_BASE = "https://api.placestovisitindia.com";

// Static frontend cards in the "Browse Sample Itineraries" section.
// Admin uploads a PDF per slot; key MUST match the backend's allow-list.
const SAMPLE_LOCATIONS = [
  { key: "jaipur",    label: "Jaipur",    region: "Rajasthan",   subtitle: "The Pink City · 4 Days / 3 Nights" },
  { key: "jaisalmer", label: "Jaisalmer", region: "Rajasthan",   subtitle: "The Golden City · 3 Days / 2 Nights" },
  { key: "udaipur",   label: "Udaipur",   region: "Rajasthan",   subtitle: "City of Lakes · 4 Days / 3 Nights" },
  { key: "rishikesh", label: "Rishikesh", region: "Uttarakhand", subtitle: "Yoga Capital · 3 Days / 2 Nights" },
];

const sectionCard = {
  background: "#fff", borderRadius: 10, padding: 22, marginBottom: 24,
  border: "1px solid #e2e5ed", boxShadow: "0 2px 6px rgba(0,0,0,.03)",
};
const sectionTitle = {
  fontSize: 18, fontWeight: 700, color: "#008c93",
  marginBottom: 6, paddingBottom: 8, borderBottom: "1px solid #e2e5ed",
};
const subtitle = { fontSize: 13, color: "#6c757d", marginBottom: 14 };
const addBtn = {
  background: "#008c93", color: "#fff", border: "none",
  padding: "10px 24px", borderRadius: 6, fontSize: 14, cursor: "pointer",
};

const idsToArray = (s) =>
  String(s || "")
    .split(",")
    .map((x) => parseInt(x, 10))
    .filter(Boolean);

const Itinerary = () => {
  const [packages, setPackages]       = useState([]);
  const [tourIds, setTourIds]         = useState([]);
  const [sampleFiles, setSampleFiles]   = useState({}); // { jaipur: { url, filename } | null, ... }
  const [sampleImages, setSampleImages] = useState({}); // same shape, for card images
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);

  useEffect(() => {
    Promise.all([loadPackages(), loadSettings()]).finally(() => setLoading(false));
  }, []);

  const loadPackages = () =>
    new Promise((resolve) => {
      GET_WITH_TOKEN("/api/packages", "",
        (res) => { setPackages(Array.isArray(res) ? res : (res?.data || [])); resolve(); },
        (e)   => { showToast(e?.message || "Failed to load packages"); resolve(); },
        (e)   => { showToast(e || "Network error"); resolve(); });
    });

  const loadSettings = () =>
    new Promise((resolve) => {
      GET_WITH_TOKEN("/api/admin/itinerary", "",
        (res) => {
          const d = res?.data || {};
          setTourIds(idsToArray(d.tour_package_ids));
          setSampleFiles(d.sample_files || {});
          setSampleImages(d.sample_images || {});
          resolve();
        },
        (e) => { showToast(e?.message || "Failed to load itinerary settings"); resolve(); },
        (e) => { showToast(e || "Network error"); resolve(); });
    });

  // PUT JSON for tour-package selection only. Sample file uploads use the
  // multipart endpoint inside <SamplePdfUploader />.
  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/itinerary`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("Token")}`,
        },
        body: JSON.stringify({ tour_package_ids: tourIds }),
      });
      const body = await res.json();
      if (res.ok) showToast("Tour packages saved");
      else        showToast(body?.message || "Save failed");
    } catch (e) {
      showToast(e?.message || "Network error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2">Loading…</p>
      </div>
    );
  }

  return (
    <div className="container mt-3" style={{ paddingBottom: 80 }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 style={{ color: "#008c93", margin: 0 }}>Itinerary Page Settings</h3>
        <button
          onClick={save}
          disabled={saving}
          style={{ ...addBtn, padding: "10px 28px", fontSize: 15, opacity: saving ? 0.6 : 1 }}
        >
          {saving ? "Saving…" : "Save Tour Packages"}
        </button>
      </div>

      <SampleFilesEditor
        sampleFiles={sampleFiles}
        sampleImages={sampleImages}
        onChangeFiles={setSampleFiles}
        onChangeImages={setSampleImages}
      />

      <PackagePicker
        title="Find Well-Curated Tour Packages Instead"
        help="Pick packages to show in the tour packages section at the bottom of the itinerary page."
        packages={packages}
        selectedIds={tourIds}
        onChange={setTourIds}
      />

      <div className="text-end">
        <button
          onClick={save}
          disabled={saving}
          style={{ ...addBtn, padding: "12px 36px", fontSize: 16, opacity: saving ? 0.6 : 1 }}
        >
          {saving ? "Saving…" : "Save Tour Packages"}
        </button>
      </div>
    </div>
  );
};

const SampleFilesEditor = ({ sampleFiles, sampleImages, onChangeFiles, onChangeImages }) => (
  <div style={sectionCard}>
    <div style={sectionTitle}>Browse Sample Itineraries — Uploads</div>
    <div style={subtitle}>
      The 4 cards on the public itinerary page are fixed. For each location,
      upload a card image (jpg/png/webp) and a sample PDF. If no image is
      uploaded the card uses a default photo; if no PDF, the button shows
      "Coming soon".
    </div>

    <div className="row g-3">
      {SAMPLE_LOCATIONS.map((loc) => (
        <div key={loc.key} className="col-md-6">
          <div style={{ border: "1px solid #e2e5ed", borderRadius: 8, padding: 14, background: "#f7f9fb" }}>
            <div className="mb-2">
              <div style={{ fontSize: 11, fontWeight: 700, color: "#008c93", letterSpacing: 1, textTransform: "uppercase" }}>
                {loc.region}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{loc.label}</div>
              <div style={{ fontSize: 12, color: "#6c757d" }}>{loc.subtitle}</div>
            </div>

            <SampleImageUploader
              loc={loc}
              current={sampleImages?.[loc.key] || null}
              onResult={onChangeImages}
            />

            <div style={{ height: 1, background: "#e2e5ed", margin: "12px 0" }} />

            <SamplePdfUploader
              loc={loc}
              current={sampleFiles?.[loc.key] || null}
              onResult={onChangeFiles}
            />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SampleImageUploader = ({ loc, current, onResult }) => {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/.test(file.type)) {
      showToast("Please choose a JPG, PNG or WEBP image.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("location", loc.key);
      fd.append("file", file);
      const res = await fetch(`${API_BASE}/api/admin/itinerary/sample-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
        body: fd,
      });
      const body = await res.json();
      if (res.ok) {
        showToast(`Image uploaded for ${loc.label}`);
        if (body?.data) onResult(body.data);
      } else {
        const err = body?.error?.file?.[0] || body?.message || "Upload failed";
        showToast(err);
      }
    } catch (e) {
      showToast(e?.message || "Network error");
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!current) return;
    if (!window.confirm(`Remove the image for ${loc.label}?`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/itinerary/sample-image/${loc.key}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
      });
      const body = await res.json();
      if (res.ok) {
        showToast(`Image removed for ${loc.label}`);
        if (body?.data) onResult(body.data);
      } else {
        showToast(body?.message || "Delete failed");
      }
    } catch (e) {
      showToast(e?.message || "Network error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#6c757d", marginBottom: 6 }}>
        Card image
      </div>
      <div className="mb-2" style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <div style={{
          width: 90, height: 60, borderRadius: 6, overflow: "hidden",
          background: "#e9ecef", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 11, color: "#6c757d",
        }}>
          {current?.url
            ? <img src={current.url} alt={loc.label}
                   style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : "No image"}
        </div>
        <div style={{ fontSize: 12, color: "#6c757d", flex: 1, wordBreak: "break-all" }}>
          {current?.filename || "Default image will be used on the frontend."}
        </div>
      </div>
      <div className="d-flex" style={{ gap: 8 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: "none" }}
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          style={{ ...addBtn, padding: "6px 14px", fontSize: 13, opacity: busy ? 0.6 : 1 }}
        >
          {busy ? "Uploading…" : (current ? "Replace image" : "Upload image")}
        </button>
        {current && (
          <button
            onClick={remove}
            disabled={busy}
            style={{ background: "#dc3545", color: "#fff", border: "none",
                     padding: "6px 14px", borderRadius: 6, fontSize: 13, cursor: "pointer",
                     opacity: busy ? 0.6 : 1 }}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

const SamplePdfUploader = ({ loc, current, onResult }) => {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      showToast("Please choose a PDF file.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("location", loc.key);
      fd.append("file", file);
      const res = await fetch(`${API_BASE}/api/admin/itinerary/sample-file`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
        body: fd,
      });
      const body = await res.json();
      if (res.ok) {
        showToast(`Uploaded sample for ${loc.label}`);
        if (body?.data) onResult(body.data);
      } else {
        const err = body?.error?.file?.[0] || body?.message || "Upload failed";
        showToast(err);
      }
    } catch (e) {
      showToast(e?.message || "Network error");
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!current) return;
    if (!window.confirm(`Remove the sample PDF for ${loc.label}?`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/itinerary/sample-file/${loc.key}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
      });
      const body = await res.json();
      if (res.ok) {
        showToast(`Removed sample for ${loc.label}`);
        if (body?.data) onResult(body.data);
      } else {
        showToast(body?.message || "Delete failed");
      }
    } catch (e) {
      showToast(e?.message || "Network error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#6c757d", marginBottom: 6 }}>
        Sample PDF
      </div>
      <div className="mb-2">
        {current ? (
          <a href={current.url} target="_blank" rel="noopener noreferrer"
             style={{ fontSize: 13, color: "#008c93", textDecoration: "none" }}>
            📄 {current.filename}
          </a>
        ) : (
          <span style={{ fontSize: 13, color: "#6c757d" }}>No PDF uploaded yet</span>
        )}
      </div>
      <div className="d-flex" style={{ gap: 8 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          style={{ display: "none" }}
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          style={{ ...addBtn, padding: "6px 14px", fontSize: 13, opacity: busy ? 0.6 : 1 }}
        >
          {busy ? "Uploading…" : (current ? "Replace PDF" : "Upload PDF")}
        </button>
        {current && (
          <button
            onClick={remove}
            disabled={busy}
            style={{ background: "#dc3545", color: "#fff", border: "none",
                     padding: "6px 14px", borderRadius: 6, fontSize: 13, cursor: "pointer",
                     opacity: busy ? 0.6 : 1 }}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

const PackagePicker = ({ title, help, packages, selectedIds, onChange }) => {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return packages;
    return packages.filter((p) =>
      [p.title, p.slug, p?.category?.name].some((v) =>
        String(v || "").toLowerCase().includes(q))
    );
  }, [packages, search]);

  const toggle = (id) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const move = (id, dir) => {
    const i = selectedIds.indexOf(id);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= selectedIds.length) return;
    const next = [...selectedIds];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const pkgById = useMemo(() => {
    const m = {};
    packages.forEach((p) => { m[p.id] = p; });
    return m;
  }, [packages]);

  return (
    <div style={sectionCard}>
      <div style={sectionTitle}>{title}</div>
      <div style={subtitle}>{help}</div>

      <div className="mb-3">
        <strong style={{ fontSize: 13 }}>
          Selected ({selectedIds.length})
          <span style={{ color: "#6c757d", fontWeight: 400 }}> — use ↑ ↓ to reorder</span>
        </strong>
        {selectedIds.length === 0 ? (
          <div className="text-muted small mt-1">No packages selected yet.</div>
        ) : (
          <div className="mt-2" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {selectedIds.map((id, idx) => {
              const p = pkgById[id];
              return (
                <div key={id}
                     style={{ display: "flex", alignItems: "center", gap: 8,
                              background: "#f7f9fb", border: "1px solid #e2e5ed",
                              borderRadius: 6, padding: "8px 12px" }}>
                  <span style={{ color: "#6c757d", fontSize: 12, width: 22 }}>{idx + 1}.</span>
                  <span style={{ flex: 1, fontSize: 14 }}>
                    {p ? p.title : `#${id}`}
                    {p?.category?.name && (
                      <span style={{ color: "#6c757d", fontSize: 12 }}> — {p.category.name}</span>
                    )}
                    {!p && <span style={{ color: "#dc3545", fontSize: 12 }}> (not found)</span>}
                  </span>
                  <button className="btn btn-sm btn-outline-secondary" style={{ padding: "2px 8px" }}
                          disabled={idx === 0} onClick={() => move(id, -1)}>↑</button>
                  <button className="btn btn-sm btn-outline-secondary" style={{ padding: "2px 8px" }}
                          disabled={idx === selectedIds.length - 1} onClick={() => move(id, 1)}>↓</button>
                  <button className="btn btn-sm btn-outline-danger" style={{ padding: "2px 10px" }}
                          onClick={() => toggle(id)}>×</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <input
        className="form-control mb-2"
        placeholder="Search packages by title, slug, or category…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #e2e5ed",
                    borderRadius: 6, padding: 8, background: "#fff" }}>
        {filtered.length === 0 ? (
          <div className="text-muted small p-2">No packages match your search.</div>
        ) : (
          filtered.map((p) => {
            const checked = selectedIds.includes(p.id);
            return (
              <label key={p.id}
                     style={{ display: "flex", alignItems: "center", gap: 10,
                              padding: "6px 4px", cursor: "pointer",
                              borderBottom: "1px solid #f0f2f5" }}>
                <input type="checkbox" checked={checked} onChange={() => toggle(p.id)} />
                <span style={{ flex: 1, fontSize: 14 }}>
                  {p.title}
                  {p?.category?.name && (
                    <span style={{ color: "#6c757d", fontSize: 12 }}> — {p.category.name}</span>
                  )}
                </span>
                {p?.price != null && (
                  <span style={{ color: "#008c93", fontSize: 12 }}>
                    ₹{p.price}{p.price_unit ? ` ${p.price_unit}` : ""}
                  </span>
                )}
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Itinerary;
